import { Head, router } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import ScheduleForm from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface UserOption {
  id: number
  name: string
}

interface ScheduleData {
  id: number
  title: string
  notes: string
  start_date: string | null
  end_date: string | null
  no_start: boolean
  no_end: boolean
  leader_id: number | null
  investigator_ids: number[]
}

interface Props {
  case_record: CaseRecord
  schedule: ScheduleData
  leaders: UserOption[]
  investigators: UserOption[]
  can_delete: boolean
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  schedule,
  leaders,
  investigators,
  can_delete,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Schedule", href: `/cases/${case_record.id}/schedules` },
    {
      title: "Edit",
      href: `/cases/${case_record.id}/schedules/${schedule.id}/edit`,
    },
  ]

  const { data, setData, patch, processing, transform } = useForm<ScheduleData>(
    {
      id: schedule.id,
      title: schedule.title,
      notes: schedule.notes,
      start_date: schedule.start_date,
      end_date: schedule.end_date,
      no_start: schedule.no_start,
      no_end: schedule.no_end,
      leader_id: schedule.leader_id,
      investigator_ids: schedule.investigator_ids,
    },
  )

  transform((data) => ({ case_schedule: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/cases/${case_record.id}/schedules/${schedule.id}`)
  }

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this schedule entry?")) {
      router.delete(`/cases/${case_record.id}/schedules/${schedule.id}`)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Schedule - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Update Schedule for {case_record.display_name}
          </h1>
          {can_delete && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-hpi-red hover:underline"
            >
              Delete
            </button>
          )}
        </div>
        <ScheduleForm
          caseId={case_record.id}
          scheduleId={schedule.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Save"
          leaders={leaders}
          investigators={investigators}
        />
      </div>
    </AppLayout>
  )
}
